/* eslint-disable */
import {
  PONG,
  DATA_CHANNEL_MSG_TYPE_VIDEO_START,
  DATA_CHANNEL_MSG_TYPE_VIDEO_FINAL,
  DATA_CHANNEL_MSG_TYPE_AUDIO,
  MAX_MESSAGE_LENGTH,
} from './constants';
import {
  LiveRequestMessage,
  LiveRequestMessageOptions,
  PlaybackRequestMessage,
  PlaybackMessageOptions,
} from './RequestMessage';
import { parseVideoPacket, Segment } from './VideoPacket';

interface AvaTurnConfig {
  expiry: number;
  servers: {
    username: string;
    password: string;
    urls: string[];
  }[];
}

export type BinaryFrame = ArrayBuffer;

const MAX_BUFFERED_SECONDS = 60;

export class AvaConnection {
  public onPlaybackError?: (error: string) => void;

  label: string;
  ws: WebSocket | null;
  iceServers: RTCIceServer[];
  pc: RTCPeerConnection | null;
  videoElement: HTMLVideoElement | null;
  mediaSource: MediaSource | null;
  sourceBuffer: SourceBuffer | null;
  partialVideo: Uint8Array | null;
  segmentQueue: Segment[] = [];
  firstDate: Date | null;
  chan: RTCDataChannel | null;
  receivingData: boolean = false;

  constructor(
    label: string,
    options: {
      turnConfig?: AvaTurnConfig;
      videoElement?: HTMLVideoElement;
      onPlaybackError?: (error: string) => void;
    },
  ) {
    this.label = label;
    this.ws = null;
    this.iceServers = [];
    this.pc = null;
    this.videoElement = null;
    this.mediaSource = null;
    this.sourceBuffer = null;
    this.partialVideo = null; // this should probably live in an abstraction somewhere
    this.firstDate = null;
    this.chan = null;

    if (options.turnConfig) this.setTurnConfig(options.turnConfig);
    if (options.videoElement) this.attachToVideoElement(options.videoElement);
    if (options.onPlaybackError) this.onPlaybackError = options.onPlaybackError;
  }

  // easy peasy setup
  async bootstrap(wsUrl: string) {
    // don't try to do these in parallel, order matters and race conditions are aplenty
    await this.connectToControlPlane(wsUrl);
    await this.connectToDataPlane();
    await this.setupSourceBuffer();
    await this.setupDataChannel();
  }

  // connect to the websocket server (used to boostrap the WebRTC connection)
  connectToControlPlane(wsUrl: string) {
    return new Promise((resolve, reject) => {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        resolve(this.ws);
      };

      this.ws.onerror = (error) => {
        reject(error);
      };
    });
  }

  // tell the client which TURN servers to use as part of it's WebRTC journey
  setTurnConfig(turnConfig: AvaTurnConfig) {
    // for whatever reason the Ava TURN API returns with the "password"
    // attribute instead of the "credential spec"
    this.iceServers = (turnConfig.servers || []).map(
      ({ username, password, urls }): RTCIceServer => ({
        username,
        credential: password,
        urls,
      }),
    );
    return Promise.resolve(this.iceServers);
  }

  // called when a new websocket message comes in
  private async onWSMessage(event: MessageEvent) {
    if (!this.pc) return;

    // intentionally not typing this on the way in because at runtime it won't be type-enforced
    const message = JSON.parse(event.data);

    switch (message.type) {
      case 'webrtc_add_candidate':
        if (message.candidate) {
          this.pc.addIceCandidate(message.candidate);
        } else if (message.candidate === null) {
          // we're done collecting candidates
        }

        break;
      case 'webrtc_offer':
        // server sends us an offer + description

        this.pc.setRemoteDescription(
          new RTCSessionDescription(message.session_description),
        );

        // create answer and send it back
        const answer = await this.pc.createAnswer();
        this.pc.setLocalDescription(answer);

        await this.wsSendJson({
          type: 'webrtc_answer',
          session_description: answer,
        });

        break;
      case 'ping':
        // server is checking to make sure we're still here
        // (we are)
        await this.wsSendJson(PONG);

        break;
      case 'webrtc_speed_test_result':
        // todo
        // message.speed is a number (what that number means I have no earthly idea)

        break;
      case 'metadata':
        // todo
        // apparently metadata.msg can be either a string or not a string? let's see if we're actually hitting this case before I dig too far in

        break;
      case 'metadata_partial':
        // todo
        /*
        liveData.partialMetadata = liveData.partialMetadata.concat(
          message.msg
        );
        if (liveData.partialMetadata.length > MAX_MESSAGE_LENGTH) {
          error(
            `Partial metadata message is currently too long. Length is ${liveData.partialMetadata.length}`
          );
          liveData.partialMetadata = "";
        }
        */

        break;
      case 'time':
        // todo
        // not even sure of message shape on this one

        break;
      case 'audio_level':
        // todo
        // not even sure of message shape on this one

        break;
      case 'sampling_rate':
        // todo
        // message.msg.samplingRate seems (?) to be a stringified

        break;
      case 'webrtc_error': {
        if (this.onPlaybackError) {
          this.onPlaybackError(message.error);
        }
        return;
      }
      default:
        break;
    }
  }

  // called when a new iceCandidate is available (and sends that candidate right back to the server)
  private async onIceCandidate(event: RTCPeerConnectionIceEvent) {
    await this.wsSendJson({
      type: 'webrtc_add_candidate',
      candidate: event.candidate,
    });
  }

  // connect to the server via webrtc datachannel (bootstrapped prior using WebRTC)
  connectToDataPlane() {
    if (this.ws === null || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error(
        'You must connect to control plane prior to attempting to connect to data plane',
      );
    }

    if (this.iceServers.length === 0) {
      // Warning: No ice servers
    }

    // create the PeerConnection object (we will bootstrap it via WS control plane below)
    this.pc = new RTCPeerConnection({
      iceServers: this.iceServers,
    });

    // listen for messages from the control plane
    this.ws.addEventListener('message', this.onWSMessage.bind(this));

    // reply back w/ our ice candidates
    this.pc.addEventListener('icecandidate', this.onIceCandidate.bind(this));

    return Promise.resolve(this.pc);
  }

  // tell it where to output
  attachToVideoElement(videoElement: HTMLVideoElement) {
    this.videoElement = videoElement;
    this.videoElement.oncanplaythrough = () => {
      this.videoElement?.pause();
      setTimeout(() => {
        // introduce a little artificial delay to give some buffer time
        this.videoElement?.play();
      }, 1000);
    };
  }

  // it's complicated but TLDR mediasource buffers can get overloaded so instead of spamming
  // it with appendBuffer, we build up a little queue in memory, combine those frames together, and
  // then appendBuffer THOSE- which makes the browser a lot happier
  private async flush() {
    if (
      !this.sourceBuffer ||
      !this.mediaSource ||
      this.mediaSource.readyState !== 'open'
    ) {
      return;
    }

    if (this.segmentQueue.length > 0) {
      if (this.sourceBuffer.updating) return;

      let val: Uint8Array | null = null;
      if (this.segmentQueue.length > 1) {
        let totalLength = this.segmentQueue.reduce(
          (acc, s) => acc + s.length,
          0,
        );
        val = new Uint8Array(totalLength);
        let offset = 0;
        for (const s of this.segmentQueue) {
          val.set(s, offset);
          offset += s.length;
        }
        this.segmentQueue = [];
      } else {
        val = this.segmentQueue.shift() || null;
      }

      if (val) {
        try {
          this.sourceBuffer.appendBuffer(val as unknown as ArrayBuffer);
        } catch (e) {
          // appendBuffer failed after teardown/closed sourceBuffer
        }
      }
    }
  }

  // called when a new data channel is created
  // then checks if that data channel label matches the label we care about
  // if it does, it attaches a message listener
  // if not, it ignores it
  async setupDataChannel() {
    if (!this.pc) {
      throw new Error(
        'Missing peerConnection, it must be set prior to trying to listen for data channel',
      );
    }

    this.pc.addEventListener('datachannel', (event) => {
      const channel = event.channel;

      if (channel.label === this.label) {
        const setReceivingDataFalse = () => {
          this.receivingData = false;
        };

        //Clean up the previous channel
        if (this.chan) {
          this.chan.removeEventListener(
            'message',
            this.onDataChannelMessage.bind(this),
          );
          this.chan.removeEventListener('close', setReceivingDataFalse);
          this.chan.close();
        }

        //Set up the new channel
        this.chan = channel;
        this.receivingData = true;
        channel.addEventListener(
          'message',
          this.onDataChannelMessage.bind(this),
        );
        channel.addEventListener('close', setReceivingDataFalse);
      }
    });
  }

  // called on every data message
  // first parses the message, figures out if it's binary or JSON
  // if it's binary, it uses the provided bytes (and heder info) to start to assemble segments
  // that will later get passed to onBinaryFrame as full frames
  //
  // a lot of this is just copied/pasted from vaion
  async onDataChannelMessage(message: MessageEvent<ArrayBuffer>) {
    if (!this.receivingData) {
      return;
    }
    const { data } = message;

    const header = new Uint8Array(data.slice(0, 1))[0];
    const body = new Uint8Array(data.slice(1));

    // Data plane messages consist of two types of WebSocket data, text and binary.
    if (data === null) {
      throw new Error('null frame');
    } else if (header < 10) {
      // Binary messages contain video data. As the messages are limited in size, first they must be reassembled.
      // Messages are in order, and there is a 1-byte header on each message to aid with reconstruction.

      if (header & DATA_CHANNEL_MSG_TYPE_AUDIO) {
        // Audio is intentionally disabled for this player path.
        return;
      }

      let partialVideo = this.partialVideo;
      if (header & DATA_CHANNEL_MSG_TYPE_VIDEO_START) {
        this.partialVideo = body;
      } else if (partialVideo !== null) {
        const n = new Uint8Array(partialVideo.length + body.length);
        n.set(partialVideo);
        n.set(body, partialVideo.length);
        this.partialVideo = n;
      }

      partialVideo = this.partialVideo;
      if (partialVideo != null) {
        if (header & DATA_CHANNEL_MSG_TYPE_VIDEO_FINAL) {
          this.onBinaryFrame(partialVideo.buffer as ArrayBuffer);
          this.partialVideo = null; // reset
        } else if (partialVideo.length > MAX_MESSAGE_LENGTH) {
          this.partialVideo = null; // reset
        }
      }
    } else {
      // Text messages are metadata in JSON format, including various messages such as bounding box information
      try {
        const text = new TextDecoder('utf-8').decode(data);
        this.onTextMessage(text);
      } catch {
        // Error decoding text message
      }
    }
  }

  // called when the data channel sends us a binary frame (like video)
  private async onBinaryFrame(data: BinaryFrame) {
    if (!this.receivingData) {
      return;
    }
    const packetData = parseVideoPacket(data);

    if (packetData.codec_type === 'h264') {
      if (packetData?.segment && this.sourceBuffer) {
        this.segmentQueue.push(packetData.segment);
        if (this.segmentQueue.length > 1) {
          this.flush();
        }
      }
    }
  }

  // called with the data channel sends us text (we don't have docs on this)
  // these look to be objects 99% of the time
  async onTextMessage(text: string) {
    // console.log("got text", string);
  }

  // MediaSource is a little-known API that allows us to assemble raw frames into playable video- neat!
  async setupSourceBuffer() {
    return new Promise<void>((resolve, reject) => {
      if (!this.videoElement) {
        return reject(
          new Error(
            'Missing videoElement, it must be set prior to setting up source buffer',
          ),
        );
      }

      if (!MediaSource) {
        return reject(new Error('Browser does not support MediaSource'));
      }

      const mediaSource = new MediaSource();

      // attach video to mediasource
      this.videoElement.src = URL.createObjectURL(mediaSource);
      this.mediaSource = mediaSource;

      // todo: listen for errors and turn them into reject

      mediaSource.addEventListener('sourceopen', () => {
        if (mediaSource.sourceBuffers.length > 0) {
          resolve();
          return;
        }
        const sourceBuffer = mediaSource.addSourceBuffer(
          'video/mp4; codecs="avc1.4d400d"', // h264
        );

        this.sourceBuffer = sourceBuffer;
        this.sourceBuffer.addEventListener('updateend', () =>
          this.manageBufferLength(),
        );

        resolve();
      });
    });
  }

  private manageBufferLength() {
    const TIME_TO_LEAVE_IN_BUFFER = 15; // This is how much time I want to buffer before the current time

    if (this.sourceBuffer && !this.sourceBuffer.updating && this.videoElement) {
      this.sourceBuffer.onupdateend = null;

      const range = this.sourceBuffer.buffered;
      const bufferIsFull =
        range.length > 0 &&
        range.end(0) - range.start(0) > MAX_BUFFERED_SECONDS;

      if (bufferIsFull) {
        // If the video is paused and data between 'first packet buffered' and 'current time' is less or equal to TIME_TO_LEAVE_IN_BUFFER
        // I do not want to receive more packets
        const secondsInBufferBeforeCurrentTime =
          this.videoElement.currentTime - range.start(0);
        if (
          this.videoElement.paused &&
          secondsInBufferBeforeCurrentTime <= TIME_TO_LEAVE_IN_BUFFER
        ) {
          this.stopReceivingPackets();
        } else {
          // Remove the data from the beginning of the buffer to the current time
          // (Leaving TIME_TO_LEAVE_IN_BUFFER seconds in buffer before the current time)
          const startTimeToDelete = range.start(0);
          const endTimeToDelete =
            this.videoElement.currentTime - TIME_TO_LEAVE_IN_BUFFER;

          startTimeToDelete < endTimeToDelete &&
            this.sourceBuffer.remove(startTimeToDelete, endTimeToDelete);
        }
      }
    }
  }

  private stopReceivingPackets() {
    this.receivingData = false;
    this.chan?.close();
    this.segmentQueue = [];
  }

  // self-explanatory
  async startLiveVideo(options: LiveRequestMessageOptions) {
    const msg = new LiveRequestMessage(this.label, options);
    return this.wsSendJson(msg.toObject());
  }

  // self-explanatory
  // note endTimestamp is optional
  async startPlayback(options: PlaybackMessageOptions) {
    if (!this.firstDate) {
      this.firstDate = options.startTimestamp;
    }
    // "bump" is a poorly named function
    // tell mediasource to stop playback when we stop getting packets
    // (otherwise loading spinner goes forever at end of video)
    // ideally we find a better way to determine the end of a clip
    // also ideally we only engage this in clip mode
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    if (!this.sourceBuffer) {
      return;
    }

    const bump = () => {
      if (timeoutId) clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        if (
          this.mediaSource?.readyState === 'open' &&
          this.sourceBuffer &&
          !this.sourceBuffer.updating
        ) {
          try {
            this.mediaSource.endOfStream();
          } catch {
            // endOfStream failed
          }
        }
      }, 3000);
    };

    // Set endOfStream only when an endTimestamp is provided
    options.endTimestamp &&
      this.sourceBuffer.addEventListener('updatestart', () => bump());
    options.endTimestamp &&
      this.sourceBuffer.addEventListener('updateend', () => bump());

    this.videoElement?.addEventListener('play', () =>
      this.restartBuffering(options),
    );

    // if the clip is 10 seconds, the video player should say it's 10 seconds
    // (this is kind of imperfect), in practice the clips are usually 3-4 seconds longer
    if (options.endTimestamp && this.mediaSource) {
      const { startTimestamp, endTimestamp } = options;

      const diffMs = endTimestamp.getTime() - startTimestamp.getTime();
      const diffSeconds = diffMs / 1000;

      this.mediaSource.duration = diffSeconds;
    }

    const msg = new PlaybackRequestMessage(this.label, options);
    return this.wsSendJson(msg.toObject());
  }

  flushBuffer(options: PlaybackMessageOptions) {
    this.stopReceivingPackets();
    if (this.sourceBuffer) {
      const range = this.sourceBuffer.buffered;
      const currentTime = this.videoElement!.currentTime;

      // Most of the time the range will have only one element
      for (let i = 0; i < range.length; i++) {
        const currentTimeIsBuffered =
          range.length > 0 &&
          currentTime >= range.start(i) &&
          currentTime <= range.end(i);
        if (currentTimeIsBuffered) {
          continue;
        }

        // Remove the data from the beginning of the buffer to the current time
        if (
          this.mediaSource?.readyState === 'open' &&
          !this.sourceBuffer.updating
        ) {
          this.sourceBuffer?.abort();
        }
        this.segmentQueue = [];
        this.sourceBuffer!.timestampOffset = 0;
        this.sourceBuffer.remove(range.start(i), range.end(i));

        if (!this.sourceBuffer.updating) {
          this.sourceBuffer.remove(range.start(i), range.end(i));
        }
      }
    }
    this.restartBuffering(options);
  }

  restartBuffering(options: PlaybackMessageOptions) {
    if (this.sourceBuffer && !this.receivingData) {
      const currentTime = this.videoElement!.currentTime;
      const range = this.sourceBuffer.buffered;
      const currentTimeIsBuffered =
        range.length > 0 &&
        currentTime >= range.start(0) &&
        currentTime <= range.end(0);

      if (currentTimeIsBuffered) {
        const lastBufferedTime = this.sourceBuffer!.buffered.end(0);
        this.sendNewPlaybackRequest(options, lastBufferedTime);
      } else {
        this.sendNewPlaybackRequest(options, currentTime);
      }
    }
  }

  sendNewPlaybackRequest(
    options: PlaybackMessageOptions,
    playbackStartOffset: number,
  ) {
    // Calculate the new start timestamp based on the current buffered time
    const newDate = new Date(
      this.firstDate!.getTime() + playbackStartOffset * 1000,
    );
    const newOptions = { ...options, startTimestamp: newDate };

    const tryStartPlayback = () => {
      if (
        this.mediaSource?.readyState === 'open' &&
        this.sourceBuffer &&
        !this.sourceBuffer.updating
      ) {
        try {
          this.sourceBuffer.abort();
          // Clear the source buffer and set the timestamp offset to the current time
          this.sourceBuffer.timestampOffset = playbackStartOffset;
          this.segmentQueue = [];

          // Close current channel and create new label. This will make that the new channel is set in datachannel event listener
          const label = generateUniqueLabel();
          this.label = label;

          // Send the new playback request
          const msg = new PlaybackRequestMessage(this.label, newOptions);
          this.wsSendJson(msg.toObject());
        } catch {
          // Error while setting timestampOffset
        }
      } else {
        this.sourceBuffer?.addEventListener('updateend', tryStartPlayback, {
          once: true,
        });
      }
    };

    tryStartPlayback();
  }

  // call this after you're 100% done, it will stop all new frames from loading
  teardown() {
    // pause the player
    this.videoElement?.pause();

    this.receivingData = false;
    this.segmentQueue = [];

    if (this.mediaSource?.readyState === 'open') {
      try {
        this.mediaSource.endOfStream();
      } catch {}
    }

    if (this.mediaSource) {
      const sourceBufferList = this.mediaSource.activeSourceBuffers;
      if (sourceBufferList) {
        for (const sb of sourceBufferList) {
          try {
            this.mediaSource.removeSourceBuffer(sb);
          } catch {}
        }
      }
    }

    this.mediaSource = null;
    this.sourceBuffer = null;

    // close peer connection
    this.pc?.close();
    this.pc = null;
    // clean up websockets
    this.ws?.close();
    this.ws = null;
  }

  // util function
  private wsSendJson(obj: object) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }
    this.ws.send(JSON.stringify(obj));
  }
}

export const generateUniqueLabel = () => `op-${globalThis.crypto.randomUUID()}`;
